"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { Hourglass } from "lucide-react"
import { Button } from "@/components/ui/button"
import { resolveAllClocks } from "@/lib/breach/deadlines"
import {
  DATA_TYPES, DEMANDS, MAGNITUDES, ORG_TYPES, PAID_OPTIONS, openQuestions, shown, withAnswer,
  type Demand, type Magnitude, type OpenQuestion, type QuestionId, type YesNoUnknown,
} from "@/lib/breach/intake"
import { nextUnseen } from "@/lib/breach/open-question-flow"
import type { Workspace } from "@/lib/breach/workspace"
import { MultiChoice, YNU } from "./choice"
import type { GoTo } from "./incident-nav"
import type { WorkspaceUpdater } from "./incident-tools"

/** Yes / No / I don't know questions that can be answered with three buttons. */
const YNU_FIELDS = ["unauthorizedAccess", "dataTaken", "encrypted", "stillActive", "affectedInUS", "backupsIntact"] as const
type YnuField = (typeof YNU_FIELDS)[number]
const isYnuField = (id: QuestionId): id is YnuField => (YNU_FIELDS as readonly string[]).includes(id)

const primary = "bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90"
const field = "mt-1 w-full max-w-xs rounded border border-border bg-background px-2 py-1.5 text-sm print:hidden"

/** One question with its answer controls, right where the person is. No navigation away. */
function QuestionCard({ q, blocked, workspace, update, onDone }: {
  q: OpenQuestion
  blocked: number
  workspace: Workspace
  update: WorkspaceUpdater
  /** Move on: the person answered, or chose to skip. */
  onDone: () => void
}) {
  const i = workspace.intake
  const ynuId: YnuField | null = isYnuField(q.id) ? q.id : null
  const stamp = () => new Date().toISOString()
  const multi = q.id === "dataTypes" || q.id === "orgTypes"
  const multiAnswered = q.id === "dataTypes" ? i.dataTypes.length > 0 : q.id === "orgTypes" ? i.orgTypes.length > 0 : false

  return (
    <div className="grid gap-2">
      <p className="text-sm font-semibold leading-snug">{q.prompt}</p>
      <p className="text-xs text-muted-foreground">{q.why}</p>
      {blocked > 0 && (
        <span className="inline-flex w-fit items-center gap-1 rounded bg-yellow-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
          <Hourglass className="h-3.5 w-3.5" aria-hidden /> Blocks {blocked} deadline clock{blocked === 1 ? "" : "s"}
        </span>
      )}

      {ynuId && (
        <div className="mt-1 flex flex-wrap gap-2 print:hidden" role="group" aria-label={q.prompt}>
          {YNU.map((opt) => (
            <Button key={opt.id} size="sm" variant="outline" aria-pressed={shown(i, ynuId) === opt.id}
              onClick={() => { update((x) => ({ ...x, updatedAt: stamp(), intake: withAnswer(x.intake, ynuId, opt.id as YesNoUnknown) })); onDone() }}>
              {opt.label}
            </Button>
          ))}
        </div>
      )}

      {q.id === "magnitude" && (
        <select className={field} aria-label={q.prompt} value={shown(i, "magnitude") ?? ""}
          onChange={(e) => { const v = e.target.value as Magnitude; update((x) => ({ ...x, updatedAt: stamp(), intake: withAnswer(x.intake, "magnitude", v) })); onDone() }}>
          <option value="" disabled>Choose…</option>
          {MAGNITUDES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      )}

      {q.id === "demand" && (
        <select className={field} aria-label={q.prompt} value={shown(i, "demand") ?? ""}
          onChange={(e) => { const v = e.target.value as Demand; update((x) => ({ ...x, updatedAt: stamp(), intake: withAnswer(x.intake, "demand", v) })); onDone() }}>
          <option value="" disabled>Choose…</option>
          {DEMANDS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
      )}

      {q.id === "paid" && (
        <div className="mt-1 flex flex-wrap gap-2 print:hidden" role="group" aria-label={q.prompt}>
          {PAID_OPTIONS.map((opt) => (
            <Button key={opt.id} size="sm" variant="outline" aria-pressed={shown(i, "paid") === opt.id}
              onClick={() => { update((x) => ({ ...x, updatedAt: stamp(), intake: withAnswer(x.intake, "paid", opt.id as YesNoUnknown) })); onDone() }}>
              {opt.label}
            </Button>
          ))}
        </div>
      )}

      {multi && (
        <div className="mt-1 print:hidden">
          {q.id === "dataTypes" && <MultiChoice options={DATA_TYPES} values={i.dataTypes} exclusive={["none-known"]}
            onChange={(v) => update((x) => ({ ...x, updatedAt: stamp(), intake: { ...x.intake, dataTypes: v as typeof x.intake.dataTypes } }))} />}
          {q.id === "orgTypes" && <MultiChoice options={ORG_TYPES} values={i.orgTypes} exclusive={["none"]}
            onChange={(v) => update((x) => ({ ...x, updatedAt: stamp(), intake: { ...x.intake, orgTypes: v as typeof x.intake.orgTypes } }))} />}
        </div>
      )}

      <div className="mt-1 flex flex-wrap items-center gap-2 print:hidden">
        {multi && multiAnswered && <Button size="sm" className={primary} onClick={onDone}>Done</Button>}
        <Button size="sm" variant="ghost" onClick={onDone}>Skip for now</Button>
      </div>
    </div>
  )
}

/**
 * The intake answers still open ("I don't know" or never answered), asked ONE AT A TIME, in place.
 * Single-choice answers move on automatically; multi-select questions have a Done button. Answering the
 * two clock-gating questions starts the waiting deadline clocks immediately. Nothing here sends the
 * person to another page; the "My answers" page is only for editing.
 */
export function OpenQuestionsPanel({ workspace, update, nav, variant }: {
  workspace: Workspace
  update: WorkspaceUpdater
  nav: GoTo
  variant: "full" | "compact"
}) {
  const questions = useMemo(() => openQuestions(workspace.intake), [workspace.intake])
  const clocks = useMemo(() => resolveAllClocks(workspace.intake, workspace.clockOverrides), [workspace.intake, workspace.clockOverrides])
  const blockedCount = useMemo(() => {
    const byId = new Map<QuestionId, number>()
    for (const c of clocks) for (const id of c.waitingOn) byId.set(id, (byId.get(id) ?? 0) + 1)
    return byId
  }, [clocks])

  // The question being asked is held until the person moves on, so a multi-select doesn't vanish after the
  // first checkbox. `seen` = answered or skipped during this visit.
  const [seen, setSeen] = useState<string[]>([])
  const [current, setCurrent] = useState<OpenQuestion | null>(null)
  const active = current ?? nextUnseen(questions, seen)
  const advance = () => { if (active) setSeen((s) => (s.includes(active.id) ? s : [...s, active.id])); setCurrent(null) }
  const hold = () => { if (active && current?.id !== active.id) setCurrent(active) }

  const anyBlocking = questions.some((q) => q.blocksClock)
  const [open, setOpen] = useState(anyBlocking)
  const wasBlocking = useRef(anyBlocking)
  useEffect(() => {
    if (anyBlocking && !wasBlocking.current) setOpen(true)
    wasBlocking.current = anyBlocking
  }, [anyBlocking])

  if (questions.length === 0 && !current) return null

  const body = active ? (
    <div onFocusCapture={hold} onClickCapture={hold}>
      <p className="mb-2 text-xs text-muted-foreground">{questions.length} still open</p>
      <QuestionCard key={active.id} q={active} blocked={blockedCount.get(active.id) ?? 0}
        workspace={workspace} update={update} onDone={advance} />
    </div>
  ) : (
    <div className="grid gap-2">
      <p className="text-sm">
        That&apos;s every open question for now. <strong>{questions.length}</strong> {questions.length === 1 ? "is" : "are"} still marked &ldquo;I don&apos;t know&rdquo;
        {anyBlocking ? ", and some deadline clocks are waiting on them." : "."}
      </p>
      <div className="print:hidden"><Button size="sm" variant="outline" onClick={() => setSeen([])}>Go through them again</Button></div>
    </div>
  )
  const footer = (
    <p className="mt-3 text-xs text-muted-foreground print:hidden">
      Want to change an earlier answer? <button type="button" className="underline" onClick={() => nav({ v: "answers" })}>Edit my answers</button>.
    </p>
  )

  if (variant === "compact") {
    return (
      <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)} className="rounded-lg border border-yellow-500/50 bg-yellow-500/5 p-3 print:hidden">
        <summary className="cursor-pointer text-sm font-semibold">Open questions ({questions.length})</summary>
        <div className="mt-2">{body}</div>
        {footer}
      </details>
    )
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4" aria-labelledby="open-questions-heading">
      <h2 id="open-questions-heading" className="font-semibold">Questions still open</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        &ldquo;I don&apos;t know&rdquo; is fine. Come back when you learn more. Some deadline clocks start when you answer.
      </p>
      <div className="mt-3 rounded-md border border-border bg-background/60 p-3">{body}</div>
      {footer}
    </section>
  )
}
